import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EditIcon } from '../../../assets/icons/edit-pencil.svg';
import { ReactComponent as RevenueManagementIcon } from '../../../assets/icons/sidebar/revenue-sharing-icon.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { ManagementAccountInterFace } from '../../../interface/revenue-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import {
  fetchAllAccounts,
  updateSingleAccount,
  setSortData,
  updateRevenueManagementAccountFilters,
  fetchRevenueManagementAccountFilters,
  disableRevenueManagementAccountFilterApplied,
} from '../../../redux/slices/RevenueManagementAccountSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import { getDaysBetween } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import revenueApi from '../../../utils/api/revenue';

import RevenueManagementToggle from './RevenueManageToggle';

interface ModalState {
  status: boolean;
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
}

interface ManagementAccountRevenueProperties {
  currentPage: string;
  setCurrentPage: React.Dispatch<React.SetStateAction<string>>;
}

interface EditPercentageProperties {
  id: string;
  value: number;
  accountCustomId: string;
}

/**
 * ManagementAccountRevenue page function component
 */
function ManagementAccountRevenue(properties: ManagementAccountRevenueProperties) {
  const { t, i18n } = useTranslation('revenue');
  const { currentPage, setCurrentPage } = properties;

  const dispatch = useAppDispatch();
  const inputReference = useRef<HTMLInputElement>(null);
  const {
    accounts,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filter,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.revenueAccount);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [editPercent, setEditPercent] = useState<EditPercentageProperties>({
    id: '',
    value: 0,
    accountCustomId: '',
  });

  /**
   * Function to handle edit percentage
   */
  const handleEditPercentage = (rowData: ManagementAccountInterFace) => {
    setEditPercent({
      id: rowData.id,
      value: rowData.revenuePercentage ? rowData.revenuePercentage : 0,
      accountCustomId: rowData.accountCustomId,
    });
  };

  /**
   * API call to update percentage
   */
  const handleUpdateRevenuePercentage = async () => {
    await revenueApi
      .editAccountPercentage({
        revenuePercentage: editPercent.value.toString(),
        accountCustomId: editPercent.accountCustomId,
      })
      .then(() => {
        const updatedAccount: ManagementAccountInterFace | undefined = accounts.find(
          (account) => account.id === editPercent.id,
        );
        if (updatedAccount) {
          dispatch(
            updateSingleAccount({
              ...updatedAccount,
              revenuePercentage: editPercent.value,
            }),
          );
        }
        setEditPercent({
          id: '',
          value: 0,
          accountCustomId: '',
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Function to handle click outside of input field
   * @param event
   */
  const handleOutsideClick = async (event: MouseEvent) => {
    if (editPercent.id !== '') {
      handleUpdateRevenuePercentage();
    }

    if (inputReference.current && !inputReference.current.contains(event.target as Node)) {
      setEditPercent({
        id: '',
        value: 0,
        accountCustomId: '',
      });
    }
  };

  /**
   * Function to handle percentage edit
   * @param name
   * @param value
   */
  const handleChangePercentage = (value: string) => {
    const percentage = Math.min(100, Math.max(0, Math.floor(Number(value) || 0)));
    setEditPercent({
      ...editPercent,
      value: percentage,
    });
  };

  const column = [
    {
      label: t('accountID'),
      accessorKey: 'accountCustomId' as keyof ManagementAccountInterFace,
      sortAllow: true,
    },
    {
      label: t('accountName'),
      accessorKey: 'accountName' as keyof ManagementAccountInterFace,
      sortAllow: true,
    },
    {
      label: t('percentage'),
      accessorKey: 'revenuePercentage' as keyof ManagementAccountInterFace,
      sortAllow: true,
      /**
       * Function to handle input for edit percentage
       * @param rowData
       */
      customData: (rowData: ManagementAccountInterFace) => (
        <div className="table-row-data">
          {editPercent.id === rowData.id ? (
            <span>
              <input
                ref={inputReference}
                type="number"
                value={editPercent.value || ''}
                placeholder="0"
                onChange={(event) => handleChangePercentage(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleUpdateRevenuePercentage();
                  }
                }}
                className="table-row-input-field"
              />
            </span>
          ) : (
            <div className="value-container">
              <p className="value">{rowData.revenuePercentage}</p>
              <span>%</span>
            </div>
          )}
        </div>
      ),
      columnWidth: 40,
    },
    {
      label: t('action'),
      accessorKey: '' as keyof ManagementAccountInterFace,
      sortAllow: false,
      /**
       * Function for action buttons
       * @param rowData
       */
      customData: (rowData: ManagementAccountInterFace) => (
        <div className="action-button-group">
          <div
            className="action-button-item view-color"
            onClick={() => handleEditPercentage(rowData)}
            role="presentation">
            <EditIcon />
          </div>
        </div>
      ),
      columnWidth: 60,
    },
  ];

  /**
   * Column defination for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('accountID'), accessorKey: 'accountCustomId' },
    { label: t('accountName'), accessorKey: 'accountName' },
    { label: t('percentage'), accessorKey: 'revenuePercentage' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  const [modalView, setModalView] = useState<ModalState>({
    status: false,
    title: '',
    header: true,
    content: <div />,
    rightCorner: '',
  });

  /**
   * Fetch Users list function
   */
  const fetchAccountsData = (data: any) => {
    if (getDaysBetween(data.startDate, data.endDate) > 30) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitError'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }
    if (!loading) {
      dispatch(fetchAllAccounts(data));
    }
  };

  /**
   * Modal box close function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: true,
      content: <div />,
      rightCorner: '',
    });
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    fetchAccountsData({ ...filter, ...queryParameters, ...value, sortField, sortBy });
  };
  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    const payload = { submissionStatus: 'approved', ...queryParameters };
    if (sortBy) {
      payload.sortBy = sortBy;
      payload.sortField = sortField;
    }
    revenueApi
      .fetchAccountsExport(payload)
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: ManagementAccountInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'accountCustomId':
                return row.accountCustomId || 'N/A';

              case 'accountName':
                return row.accountName || 'N/A';

              case 'revenuePercentage':
                return `${row.revenuePercentage ?? 0}%`;

              default:
                return (row as any)[col.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'revenue-management-account-list.csv');
          } else {
            exportPDF(headers, rows, 'revenue-management-account-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchAccountsData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchAccountsData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);

    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [editPercent]);

  useEffect(() => {
    if (isFilterUpdated || accounts.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchAccountsData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableRevenueManagementAccountFilterApplied());
    }
  }, [filters]);

  useEffect(() => {
    if (editPercent.id && inputReference.current) {
      inputReference.current.focus(); // Ensure the input is focused after state update
    }
  }, [editPercent.id]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('revenueManagement'),
        icon: <RevenueManagementIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults]);

  return (
    <div className="table-content" dir={i18n.dir()}>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
      />
      <CustomTable
        header={column}
        data={accounts}
        serialNo
        version="v2"
        height={window.innerHeight >= 700 ? 337 : 266}
        loading={loading}
        page={page}
        limit={limit}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        onHiddenColumnsChange={setHiddenColumns}
        filters={filters}
        filtersToDispatch={updateRevenueManagementAccountFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchRevenueManagementAccountFilters}
        sizeChangeButtonRequired
        rightTopContent={
          <RevenueManagementToggle currentPage={currentPage} setCurrentPage={setCurrentPage} />
        }
        tableHeaderVersion="v1"
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        refreshRequired
        ReferenceCurrentPages={handleRefresh}
        handleRefresh={handleRefresh}
        hoverLabel={t('refresh')}
      />
    </div>
  );
}

export default ManagementAccountRevenue;
